/**
 * SubscriptionChoice.jsx
 * ======================
 * 
 * Écran de choix explicite : Individuel ou Entreprise
 * 
 * RÈGLES MOA PHASE 5:
 * - Individuel: Ouvre modal de sélection des packs (Découverte/Standard/Pro)
 * - Entreprise: Navigation vers parcours B2B existant
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Building2, Check, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { IndividualSubscriptionModal } from '../components/premium/IndividualSubscriptionModal';

const SubscriptionChoice = () => {
  const navigate = useNavigate();
  const [showIndividualModal, setShowIndividualModal] = useState(false);

  const handleBack = () => {
    navigate('/training');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#F9F6EE] to-[#F5EDDA] flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        {/* Back Button — Iter295 light theme */}
        <button
          onClick={handleBack}
          className="fixed top-4 left-4 md:absolute md:top-0 md:left-0 z-50 flex items-center gap-2 px-4 py-2 rounded-lg bg-white hover:bg-[#FBF8F1] backdrop-blur-md border border-[#E5DFD0] text-[#374151] hover:text-[#92710A] hover:border-[#92710A]/40 transition-all duration-300 hover:scale-105 group shadow-sm"
          aria-label="Retour au tableau de bord"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="hidden sm:inline font-medium">Retour</span>
        </button>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 mt-16 md:mt-0"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-4">
            Comment souhaitez-vous accéder à{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#92710A] via-[#D4AF37] to-[#92710A]">
              Coach DIA
            </span>
            {' '}?
          </h1>
          <p className="text-xl text-[#374151]">
            Choisissez le type de souscription adapté à vos besoins
          </p>
        </motion.div>

        {/* Cards Container */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          
          {/* Card Individuel */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -8, scale: 1.02 }}
            className="bg-white rounded-3xl p-8 border-2 border-[#E5DFD0] shadow-[0_8px_30px_-10px_rgba(146,113,10,0.15)] cursor-pointer hover:border-[#92710A]/50 hover:shadow-[0_14px_40px_-8px_rgba(146,113,10,0.25)] transition-all duration-300"
            onClick={() => setShowIndividualModal(true)}
            data-testid="individual-card"
          >
            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F3D060] via-[#D4AF37] to-[#92710A] flex items-center justify-center mb-6 shadow-md">
              <User className="w-8 h-8 text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-[#111827] mb-2">
              Individuel
            </h2>
            
            {/* Subtitle */}
            <p className="text-[#92710A] font-medium mb-4">
              À partir de 5,90€/mois
            </p>

            {/* Description */}
            <p className="text-[#374151] mb-6">
              Choisissez le nombre de sessions adapté à votre rythme d&apos;entraînement.
            </p>

            {/* Features */}
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>30, 73 ou 150 sessions/mois</span>
              </li>
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>Coach IA vocal et écrit</span>
              </li>
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>Annulation à tout moment</span>
              </li>
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>Sans engagement</span>
              </li>
            </ul>

            {/* CTA */}
            <button
              className="w-full bg-gradient-to-r from-[#92710A] via-[#D4AF37] to-[#92710A] hover:from-[#7C5D08] hover:via-[#C19B25] hover:to-[#7C5D08] text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-md"
              data-testid="choose-individual-btn"
            >
              <Sparkles className="w-5 h-5" />
              Choisir l&apos;offre Individuelle
            </button>
          </motion.div>

          {/* Card Entreprise */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ y: -8, scale: 1.02 }}
            className="bg-white rounded-3xl p-8 border-2 border-[#E5DFD0] shadow-[0_8px_30px_-10px_rgba(31,26,18,0.12)] cursor-pointer hover:border-[#1F1A12]/50 hover:shadow-[0_14px_40px_-8px_rgba(31,26,18,0.2)] transition-all duration-300 relative overflow-hidden"
            onClick={() => navigate('/subscription/enterprise')}
            data-testid="enterprise-card"
          >
            {/* Badge */}
            <div className="absolute top-4 right-4 bg-[#1F1A12] text-[#F3D060] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Équipes
            </div>

            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1F1A12] via-[#374151] to-[#1F1A12] flex items-center justify-center mb-6 shadow-md">
              <Building2 className="w-8 h-8 text-[#F3D060]" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-[#111827] mb-2">
              Entreprise
            </h2>
            
            {/* Subtitle */}
            <p className="text-[#92710A] font-medium mb-4">
              Licences par Licences • Prix sur mesure
            </p>

            {/* Description */}
            <p className="text-[#374151] mb-6">
              Gérez les accès de votre équipe avec des licences flexibles et une facturation centralisée.
            </p>

            {/* Features */}
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>N licences selon vos besoins</span>
              </li>
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>Durée flexible par licence</span>
              </li>
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>Gestion centralisée</span>
              </li>
              <li className="flex items-center gap-3 text-[#374151]">
                <Check className="w-5 h-5 text-[#92710A] flex-shrink-0" />
                <span>Facturation entreprise</span>
              </li>
            </ul>

            {/* CTA */}
            <button
              className="w-full bg-[#1F1A12] hover:bg-[#2A2319] text-[#F3D060] font-bold py-4 px-8 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-md"
              data-testid="choose-enterprise-btn"
            >
              <Building2 className="w-5 h-5" />
              Choisir l&apos;offre Entreprise
            </button>
          </motion.div>
        </div>

        {/* Footer info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-10"
        >
          <p className="text-[#374151] text-sm">
            Paiement 100% sécurisé par Stripe · Données cryptées
          </p>
        </motion.div>
      </div>

      {/* Modal Abonnement Individuel */}
      <IndividualSubscriptionModal 
        isOpen={showIndividualModal} 
        onClose={() => setShowIndividualModal(false)} 
      />
    </div>
  );
};

export default SubscriptionChoice;
